import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Divider from "@material-ui/core/Divider";
import { useDispatch } from "react-redux";
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Typography,
  ListItemText,
  TextField,
  Toolbar,
  Tooltip,
  makeStyles,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  TableContainer,
} from "@material-ui/core";
import { putData } from "../../../core/fetchService";
import { Autocomplete } from "@material-ui/lab";
import { AutocompleteFilterOptions } from "../../components/material-ui/Autocomplete";
import { fetchModelFilters } from "../../../features/modelFilters";
import { AddBox as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import Highlighter from "react-highlight-words";

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper
  },
  list: {
    marginTop: "20px",
    minHeight: "320px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.background.paper,
  },
}));

export default function Filter(props) {
  const {
    type,
    options,
    filterValues,
    entitySelectPlaceholder,
    addButtonTooltip,
    selectedFilter,
    setSelectedFilterId,
    projectId,
  } = props;
  const initialSV = type.includes('exclude')? null: {from: null, to: null};
  const initialST = type.includes('exclude')? '': {from: '', to: ''};

  const classes = useStyles();
  
  const { t } = useTranslation();
  
  const dispatch = useDispatch();

  const [selectedValue, setSelectedValue] = useState(initialSV);
  const [searchText, setSearchText] = useState(initialST);

  useEffect(() => {
    setSelectedValue(initialSV);
    setSearchText(initialST);
  }, [selectedFilter]);

  useEffect(() => { if (type.includes('exclude')) searchOption(selectedValue); }, [selectedValue]);

  // TODO: use Autocomplete's features instead of explicit searchText
  useEffect(() => { const k='from'; if (type.includes('replace')) searchOption(selectedValue[k], k); }, [selectedValue?.from]);
  useEffect(() => { const k='to';   if (type.includes('replace')) searchOption(selectedValue[k], k); }, [selectedValue?.to]);

  const handleFilterUpdate = data => 
    putData(`/api/filter/${selectedFilter?._id}`, { filter: {[type]: data} }, dispatch, data => {
      setSelectedFilterId(data.filter._id);
      dispatch(fetchModelFilters(projectId));
  });

  const filterOptions = inputName => (options, state) => {
    const f = () => {
      if (type.includes('exclude'))
        return v => 
          !selectedFilter.replaceIntents?.[v.label] && !filterValues.includes(v.label);
      
      if (type.includes('replace')) {
        const fromValues = Object.keys(filterValues);
        const toValues = Object.values(filterValues);
        return v =>
          !selectedFilter[type.replace('replace','exclude')].includes(v.label)
          && !fromValues.includes(v.label) && selectedValue.from != v.label
          && (inputName == 'to'
              || !toValues.includes(v.label) && selectedValue.to != v.label);
      }
      return undefined; // should not be here
    }
    return AutocompleteFilterOptions(options, state).filter(f());
  };

  const handleSelectedValueChange = (value, inputName) => {
    if (type.includes('exclude')) setSelectedValue(value);
    if (type.includes('replace')) setSelectedValue({...selectedValue, [inputName]: value});
  };

  const handleAddClick = (value) => {
    if (type.includes('exclude')) handleFilterUpdate([...filterValues, selectedValue].filter(Boolean));
    if (type.includes('replace')) handleFilterUpdate({ ...filterValues, ...value });

    setSelectedValue(initialSV);
  };

  const handleDeleteClick = (value, keyToDelete) => {
    if (type.includes('exclude')) handleFilterUpdate(filterValues.filter((item) => item !== value));
    if (type.includes('replace')) handleFilterUpdate(
                                    Object.entries(filterValues)
                                    .filter(([key]) => key !== keyToDelete)
                                    .reduce((map, [key, value]) => ({ ...map, [key]: value }), {}),
                                 );
  };

  const searchOption = (value, inputName) => {
    if (type.includes('exclude')) setSearchText(value || '')
    if (type.includes('replace')) setSearchText({ ...searchText, [inputName]: value || ''})
  };
  
  return (
    <>
      {type.includes('exclude')?
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Toolbar disableGutters variant="dense">
            <Autocomplete
              onBlur={() => searchOption(selectedValue)}
              onClose={() => searchOption(selectedValue)}
              inputValue={searchText}
              value={selectedValue}
              className={classes.autocomplete}
              size="small"
              options={options}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) => option.label === value}
              renderOption={(option) =>
                    <Highlighter
                      searchWords={[searchText]}
                      autoEscape={true}
                      textToHighlight={option.label}/>
                    }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={e => searchOption(e.target.value)}
                  placeholder={entitySelectPlaceholder}
                  size="small"
                  variant="outlined"
                />
              )}
              filterOptions={filterOptions()}
              onChange={(e, value) => handleSelectedValueChange(value?.label || null)}
            />
            <IconButton
              onClick={() => handleAddClick(selectedValue)}
              disabled={!selectedValue}
            >
              <Tooltip title={addButtonTooltip || ""}>
                <AddIcon fontSize="small"/>
              </Tooltip>
            </IconButton>
          </Toolbar>
        </Grid>
        <Grid item xs={12} sm={12}>
          <List disablePadding className={classes.list}>
            {filterValues.map((item) => {
              return (
                <Fragment key={item}>
                  <ListItem>
                    <ListItemText>{item}</ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteClick(item)}
                      >
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider light />
                </Fragment>
              );
            })}
          </List>
        </Grid>
      </Grid>:
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Toolbar disableGutters variant="dense">
            <Grid container>
              {['from', 'to'].map(k => (
                <Grid key={k} style={{ display: "flex", minHeight: "10px" }} item xs={6} sm={6}>
                  <Typography style={{ alignSelf: "center", margin: "0 5px" }} className={classes.label}>
                    {t("filters.replace_"+ k)}:
                  </Typography>
                  <Autocomplete
                    style={{ alignSelf: "center" }}
                    inputValue={searchText[k]}
                    value={selectedValue[k]}
                    filterOptions={filterOptions(k)}
                    onClose={() => searchOption(selectedValue[k], k)}
                    onBlur={() => searchOption(selectedValue[k], k)}
                    className={classes.autocomplete}
                    size="small"
                    options={options}
                    getOptionLabel={option => option.label || ''}
                    getOptionSelected={(option, value) => option.label == value}
                    renderOption={(option) =>
                      <Highlighter
                        searchWords={[searchText[k]]}
                        autoEscape={true}
                        textToHighlight={option.label}/>
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onChange={e => searchOption(e.target.value, k)}
                        placeholder={entitySelectPlaceholder}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    onChange={(e, value) => handleSelectedValueChange(value?.label || null, k)}
                  />
                </Grid>))}
            </Grid>
            <IconButton
              onClick={() => handleAddClick({ [selectedValue.from]: selectedValue.to })}
              disabled={!(selectedValue.from && selectedValue.to)}
            >
              <Tooltip title={addButtonTooltip}>
                <AddIcon/>
              </Tooltip>
            </IconButton>
          </Toolbar>
        </Grid>
        <Grid item xs={12} sm={12}>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow className={classes.headRow}>
                  <TableCell className={classes.headCell} align="center">
                    {t("filters.replace_from")}
                  </TableCell>
                  <TableCell
                    className={classes.headCell}
                    align="center"
                    colSpan={2}
                  >
                    {t("filters.replace_to")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(filterValues).map(([from, to]) => {
                  return (
                    <TableRow key={`${from}:${to}`}>
                      <TableCell
                        size={"small"}
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        {from}
                      </TableCell>
                      <TableCell
                        size={"small"}
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        {to}
                      </TableCell>
                      <TableCell
                        size={"small"}
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        <IconButton onClick={() => handleDeleteClick(null, from)}>
                          <DeleteIcon/>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>}
    </>
  );
};
